<template>
    <div class="dashboard flow__container-fixed flow__panel">
        <div class="dashboard__main">
            <section class="dashboard__main__panel">
                <h2 class="dashboard__main__panel__title">
                    {{ myResources.title }}
                </h2>
                <p class="dashboard__main__panel__intro">
                    {{ myResources.introText }}
                </p>
                <hh-error-display :error="errorMessage" />
            </section>
            <div v-if="profile" class="dashboard__main__content">
                <section v-if="hasAddresses"
                         class="dashboard__main__content__addresses">
                    <dashboard-address v-for="address in profile.addresses"
                                       class="dashboard__main__content__addresses__item"
                                       :key="address.id"
                                       :address="address" />
                </section>
            </div>
            <loading-animation v-else-if="!errorMessage" class="dashboard__loading" />
        </div>
        <aside class="dashboard__sidebar">
            <hh-btn class="dashboard__sidebar__button dashboard__sidebar__button--add-address"
                    :text="myResources.ctaAddAddress"
                    @click="onAddAddressClick" />
            <hh-btn v-if="showAccountSettings"
                    class="dashboard__sidebar__button dashboard__sidebar__button--account-settings"
                    def="cta"
                    :text="allResources.views.accountSettings.title"
                    @click="onAccountSettingsClick" />
        </aside>
    </div>
</template>

<script>

import UserManager from '@/user-manager'
import PropertyManager from '@/property-manager'
import Storage from '@/storage'

import InstanceTypeHelper from '@/components/instance-type-helper'
import ResourcesHelper from '@/components/resources-helper'

import LoadingAnimation from '@/components/app-core/loading-animation'
import DashboardAddress from '@/components/dashboard/dashboard-address'

export default {
    mixins: [
        InstanceTypeHelper,
        ResourcesHelper,
    ],

    data() {
        return {
            /**
             * @type {User}
             */
            profile: null,

            /**
             * @type {?string}
             */
            errorMessage: null,
        }
    },

    computed: {

        /**
         * @returns {boolean} True if ths profile is loaded and has addresses data.
         */
        hasAddresses() {
            return this.profile && this.profile.addresses && this.profile.addresses.length > 0
        },

        /**
         * Return the resources object for this page.
         * @return {DashboardResources}
         */
        myResources() {
            return this.parseResources('views.dashboard')
        },

        /**
         * True if the account settings button should be displayed.
         * @return {boolean}
         */
        showAccountSettings() {
            return UserManager.enableAccountSettings()
        },
    },

    async mounted() {
        try {
            await this._loadDataAsync()
        } catch (err) {
            this.errorMessage = `Unable to load user info: ${ err.message }`
            throw err
        }
    },

    methods: {
        async _loadDataAsync() {
            try {
                this.profile = await UserManager.loadProfileDataAsync()

                this.$logger.logObject({ profile: this.profile }, 'Loading addresses from profile', 'Dashboard')
                if (this.profile) {
                    this.$logger.log(`Setting ${ this.profile.addresses.length } addresses`, 'Dashboard')
                    PropertyManager.storeLinkedProperties(this.profile.addresses)
                }
            } catch (err) {
                this.errorMessage = `Unable to load dashboard: ${ err.message }`
                throw err
            }
        },

        /**
         * Triggers when clicking Account Settings.
         */
        onAccountSettingsClick() {
            this.$router.push({ name: 'MyAccount' })
        },

        /**
         * Triggers when clicking Create New Plan.
         */
        async onAddAddressClick() {
            await Storage.clearDataAsync()
            this.$router.push({ name: 'Search' })
        },
    },

    components: {
        LoadingAnimation,
        DashboardAddress,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_dashboard.scss

.dashboard {

    @include nested-element('main', 'content') {

        @include nested-element('addresses', 'item') {
            margin-bottom: $wide-gutter;
        }
    }

    @include nested-element('sidebar', 'button') {
        width: 100%;
        margin-bottom: 1rem;

        @include bp-min-small {
            width: auto;
        }

        @include bp-min-mid {
            width: 100%;
        }
    }
}

</style>
